import React, {
  FC,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import {
  AssistantTodoItem,
  GetAllAssistantTodosResponse,
  SubThreadCreatedEvent,
  SubThreadUpdateEvent,
  AgentStreamEvents,
  SubThreadStatus,
  ResponseArtifactDTO,
  UpdateResponseArtifactResponse,
  GetUserArtifactsResponse,
  ResponseArtifactCreatedEvent,
  ResponseArtifactUpdatedEvent,
  UserTodoItem,
  GetUserTodosResponse,
  UpdateResponseArtifactRequest,
} from '@dreamai/types';
import { usePusher } from '../../../contexts/PusherContext';
import { formatDueDate as formatDueDateLong } from '../../../utils/dueDate';

import { ReactComponent as TodoSpinnerIcon } from '../../../icons/todo-spinner.svg';
import { ReactComponent as PlusAddIcon } from '../../../icons/plus-add.svg';
import { ReactComponent as ChatThreadIcon } from '../../../icons/chat-thread.svg';
import { ReactComponent as FlagIcon } from '../../../icons/flag.svg';
import { ReactComponent as ChevronDownIcon } from '../../../icons/chevron-down.svg';
import { ReactComponent as HourglassIcon } from '../../../icons/hourglass.svg';
import { ReactComponent as ClockIcon } from '../../../icons/clock.svg';
import { ReactComponent as CalendarOutlineIcon } from '../../../icons/calendar-outline.svg';
import { ReactComponent as CheckSmallIcon } from '../../../icons/check-small.svg';
import { ReactComponent as ListViewIcon } from '../../../icons/list-view.svg';
import { ReactComponent as ProjectGroupedViewIcon } from '../../../icons/project-grouped-view.svg';
import { ReactComponent as DocumentLinesIcon } from '../../../icons/document-lines.svg';
import { ReactComponent as CheckCircleFilledIcon } from '../../../icons/check-circle-filled.svg';
import { ReactComponent as CrossIcon } from '../../../icons/cross.svg';

import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../api';
import TasksSkeleton from './TasksSkeleton';
import ErrorState from '../../common/ErrorState';
import ResponseArtifactModal from './ResponseArtifactModal';
import { isOrganisationChargeError } from './organisationSpend';
import { isHotelQuoteExpiredError } from './hotelArtifact';
import { usePlanModal } from '../../../providers/PlanModalProvider';
import ResearchBlockModal from '../canvas/ResearchBlockModal';
import { ProjectAppLoadingProvider } from '../../../providers/ProjectAppViewLoadingProvider';
import { ReactComponent as DocumentTextIcon } from '../../../icons/document-text.svg';
import DismissTodoModal from './DismissTodoModal';
import ResearchStillThereModal from './ResearchStillThereModal';
import {
  useOnboardingModals,
  useResponseArtifactEvents,
} from '../../../providers';
import './central-todo-list.scss';
import type { CardSelection } from './SpendingApprovalModal';

export interface DraggedTaskData {
  type: 'userTodo' | 'artifact';
  id: string;
  description: string;
}

interface CentralTodoListProps {
  onNavigateToThread?: (threadId: string, parentThreadId?: string) => void;
  taskFilter?: 'all' | 'user';
  variant?: 'waiting_on_user' | 'other';
  title?: string;
  agentImageUrl?: string;
  agentName?: string;
  onAddTodo?: () => void;
  optimisticUserTodo?: UserTodoItem | null;
  confirmedTodoIds?: { optimisticId: string; realId: string } | null;
  optimisticAgentTempTodo?: UserTodoItem | null;
  confirmedAgentTodoIds?: { optimisticId: string; realId: string } | null;
  onTaskDropped?: (data: DraggedTaskData) => void;
  removedUserTodoId?: string | null;
  threadId?: string | null;
  hideDone?: boolean;
  agentId?: string;
  isOutOfCredits?: boolean;
  resetsAt?: string | null;
  /** Called when a full-screen overlay (research modal, artifact modal) opens or closes */
  onOverlayChange?: (isOpen: boolean) => void;
  /** When provided, overrides the internal groupMode (used by mobile tab switcher) */
  mobileGroupMode?: 'none' | 'project-grouped' | 'notes';
}

const getGroupForTodo = (todo: {
  isArchivedThread?: boolean;
}): 'active' | 'done' => {
  return todo.isArchivedThread ? 'done' : 'active';
};

// Status priority for sorting within active group
// Lower number = higher priority (appears first)
const getStatusPriority = (status: string): number => {
  switch (status) {
    case 'user_added':
      return 0;
    case 'waiting_on_user':
      return 1;
    case 'processing':
      return 2;
    case 'waiting_on_third_party':
      return 3;
    default:
      return 4;
  }
};

/** Priority colour mapping (dot fill). */
const PRIORITY_COLORS: Record<string, string> = {
  high: '#EF4444',
  medium: '#F59E0B',
  low: '#3B82F6',
};

const formatDueDate = (dueDate: string): string =>
  formatDueDateLong(dueDate, { month: 'long' });

interface GroupedTodos {
  active: AssistantTodoItem[];
  done: AssistantTodoItem[];
}

export const CentralTodoList: FC<CentralTodoListProps> = ({
  onNavigateToThread,
  taskFilter = 'all',
  variant = 'other',
  title,
  agentImageUrl,
  agentName,
  onAddTodo,
  optimisticUserTodo,
  confirmedTodoIds,
  optimisticAgentTempTodo,
  confirmedAgentTodoIds,
  onTaskDropped,
  removedUserTodoId,
  threadId,
  hideDone,
  agentId,
  isOutOfCredits,
  resetsAt,
  onOverlayChange,
  mobileGroupMode,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const pusherChannel = usePusher();
  const { hasSeenModal } = useOnboardingModals();

  const [todos, setTodos] = useState<AssistantTodoItem[]>([]);
  const [loading, setLoading] = useState(true);
  // For waiting_on_user variant: pending artifacts (active) and actioned artifacts (done)
  const [pendingArtifacts, setPendingArtifacts] = useState<
    ResponseArtifactDTO[]
  >([]);
  const [actionedArtifacts, setActionedArtifacts] = useState<
    ResponseArtifactDTO[]
  >([]);
  const [userTodos, setUserTodos] = useState<UserTodoItem[]>([]);
  const [tempTodos, setTempTodos] = useState<UserTodoItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [groupMode, setGroupMode] = useState<
    'none' | 'project-grouped' | 'notes'
  >(() => {
    try {
      const saved = localStorage.getItem(`centralTodoListGroupMode_${variant}`);
      if (
        saved === 'none' ||
        saved === 'project-grouped' ||
        saved === 'notes'
      ) {
        return saved;
      }
    } catch {
      /* ignore */
    }
    return 'none';
  });

  const updateGroupMode = useCallback(
    (mode: 'none' | 'project-grouped' | 'notes') => {
      setGroupMode(mode);
      try {
        localStorage.setItem(`centralTodoListGroupMode_${variant}`, mode);
      } catch {
        /* ignore */
      }
    },
    [variant],
  );

  // Sync mobileGroupMode override from parent (mobile tab switcher)
  useEffect(() => {
    if (mobileGroupMode !== undefined) {
      setGroupMode(mobileGroupMode);
    }
  }, [mobileGroupMode]);
  const [notes, setNotes] = useState<ResponseArtifactDTO[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesHasMore, setNotesHasMore] = useState(false);
  const [notesLoadingMore, setNotesLoadingMore] = useState(false);
  const notesFetchedRef = useRef(false);
  const [dragOver, setDragOver] = useState(false);
  const [animatingItems, setAnimatingItems] = useState<Set<string>>(new Set());
  const [summaryChangedItems, setSummaryChangedItems] = useState<Set<string>>(
    new Set(),
  );

  // Completing animation state — items in this set show the completing animation before removal
  const [completingItems, setCompletingItems] = useState<Set<string>>(
    new Set(),
  );
  const completingItemsRef = useRef(completingItems);
  completingItemsRef.current = completingItems;

  // Submitted items — show "Reading your response" with spinner before completing animation
  const [submittedItems, setSubmittedItems] = useState<Set<string>>(new Set());
  const submittedItemsRef = useRef(submittedItems);
  submittedItemsRef.current = submittedItems;

  const todoSound = useMemo(() => new Audio('/todo-sound.mp3'), []);

  const playTodoSound = useCallback(() => {
    todoSound.currentTime = 0;
    todoSound.play().catch(() => {});
  }, [todoSound]);

  const startCompletingAnimation = useCallback(
    (todoId: string, element: HTMLElement | null, skipSound = false) => {
      const todoEl = element?.closest('.todo-item') as HTMLElement | null;
      if (todoEl) {
        todoEl.style.height = `${todoEl.offsetHeight}px`;
      }
      if (!skipSound) playTodoSound();
      setCompletingItems((prev) => new Set(prev).add(todoId));
    },
    [playTodoSound],
  );

  // Accordion state
  const [doneExpanded, setDoneExpanded] = useState(false);

  // Dismiss todo modal state
  const [dismissModalOpen, setDismissModalOpen] = useState(false);
  const [todoToDismiss, setTodoToDismiss] = useState<AssistantTodoItem | null>(
    null,
  );

  // Artifact modal state
  const [artifactModalOpen, setArtifactModalOpen] = useState(false);
  const [selectedTodo, setSelectedTodo] = useState<AssistantTodoItem | null>(
    null,
  );
  const [fullArtifact, setFullArtifact] = useState<ResponseArtifactDTO | null>(
    null,
  );
  const [loadingArtifact, setLoadingArtifact] = useState(false);

  // Research still there modal state (for blank todos with linked research)
  const [researchModalOpen, setResearchModalOpen] = useState(false);
  const [blankTodoToComplete, setBlankTodoToComplete] =
    useState<ResponseArtifactDTO | null>(null);

  // Linked research overlay state
  const [linkedResearchAppId, setLinkedResearchAppId] = useState<string | null>(
    null,
  );
  const [linkedResearchArtifactId, setLinkedResearchArtifactId] = useState<
    string | null
  >(null);
  const [linkedResearchOpen, setLinkedResearchOpen] = useState(false);

  // Notify parent when a full-screen overlay is active
  useEffect(() => {
    onOverlayChange?.(linkedResearchOpen || artifactModalOpen);
  }, [linkedResearchOpen, artifactModalOpen, onOverlayChange]);

  // Filter and group todos by status based on variant
  const groupedTodos = useMemo((): GroupedTodos => {
    const groups: GroupedTodos = {
      active: [],
      done: [],
    };

    // Filter todos based on variant
    // - waiting_on_user variant: only waiting_on_user items (user-facing todo list)
    // - other variant: all non-archived sub-threads (agent tasks list)
    const filteredTodos =
      variant === 'waiting_on_user'
        ? todos.filter((todo) => todo.status === 'waiting_on_user')
        : todos;

    filteredTodos.forEach((todo) => {
      const group = getGroupForTodo(todo);
      groups[group].push(todo);
    });

    // Sort active by status priority, then by updatedAt within same priority
    groups.active.sort((a, b) => {
      const priorityDiff =
        getStatusPriority(a.status) - getStatusPriority(b.status);
      if (priorityDiff !== 0) return priorityDiff;
      // Within same priority, sort by most recent
      const aTime = a.updatedAt || a.createdAt;
      const bTime = b.updatedAt || b.createdAt;
      return new Date(bTime).getTime() - new Date(aTime).getTime();
    });

    // Sort done by most recent
    groups.done.sort((a, b) => {
      const aTime = a.updatedAt || a.createdAt;
      const bTime = b.updatedAt || b.createdAt;
      return new Date(bTime).getTime() - new Date(aTime).getTime();
    });

    return groups;
  }, [todos, variant]);

  // Memoize artifacts array for modal to prevent unnecessary resets
  const modalArtifacts = useMemo(
    () => (fullArtifact ? [fullArtifact] : []),
    [fullArtifact],
  );

  const handleDismissClick = useCallback(
    (todo: AssistantTodoItem) => {
      if (hasSeenModal('dismiss_todo_warning' as any)) {
        // Skip modal — go straight to confirm
        handleDismissConfirm(todo);
      } else {
        setTodoToDismiss(todo);
        setDismissModalOpen(true);
      }
    },
    [hasSeenModal],
  );

  const handleDismissConfirm = useCallback(
    async (todo: AssistantTodoItem) => {
      setDismissModalOpen(false);
      setTodoToDismiss(null);

      // Capture height and start completing animation + sound
      const todoEl = document.querySelector(
        `[data-todo-id="${todo.id}"]`,
      ) as HTMLElement | null;
      startCompletingAnimation(todo.id, todoEl);

      // Remove after animation completes
      setTimeout(() => {
        setCompletingItems((prev) => {
          const next = new Set(prev);
          next.delete(todo.id);
          return next;
        });
        setTodos((prev) =>
          prev.map((t) =>
            t.id === todo.id
              ? {
                  ...t,
                  status: 'cancelled_by_user' as any,
                  summary: 'Cancelled by user',
                  isArchivedThread: true,
                }
              : t,
          ),
        );
      }, 600);

      try {
        const accessToken = await getAccessTokenSilently();
        await api.post(
          `/todos/${todo.id}/cancel`,
          {},
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
      } catch (err) {
        console.error('Error cancelling todo:', err);
      }
    },
    [getAccessTokenSilently, startCompletingAnimation],
  );
  const handleDismissModalClose = useCallback(() => {
    setDismissModalOpen(false);
    setTodoToDismiss(null);
  }, []);

  const handleNavigateToThread = useCallback(
    (todo: AssistantTodoItem) => {
      if (onNavigateToThread) {
        onNavigateToThread(todo.id, todo.parentThreadId);
      }
    },
    [onNavigateToThread],
  );

  const handleNavigateToParentThread = useCallback(
    (todo: AssistantTodoItem) => {
      if (onNavigateToThread) {
        onNavigateToThread(todo.parentThreadId, todo.parentThreadId);
      }
    },
    [onNavigateToThread],
  );

  const openArtifactModal = useCallback(
    async (todo: AssistantTodoItem) => {
      const latestArtifact = todo.latestArtifact;
      if (!latestArtifact) return;

      setSelectedTodo(todo);
      setLoadingArtifact(true);
      setArtifactModalOpen(true);

      try {
        // Fetch full artifact details using the parent thread
        const token = await getAccessTokenSilently();
        const response = await api.get<{
          success: boolean;
          artifacts: ResponseArtifactDTO[];
        }>(`/response-artifacts/${todo.parentThreadId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // Find the artifact matching this todo's latestArtifact
        const artifact = response.data.artifacts.find(
          (a) => a.id === latestArtifact.id,
        );
        if (artifact) {
          setFullArtifact(artifact);
        }
      } catch (err) {
        console.error('Failed to fetch artifact:', err);
      } finally {
        setLoadingArtifact(false);
      }
    },
    [getAccessTokenSilently],
  );

  const closeArtifactModal = useCallback(() => {
    setArtifactModalOpen(false);
    setSelectedTodo(null);
    setFullArtifact(null);
  }, []);

  const handleOptimisticAction = useCallback(
    (artifact: ResponseArtifactDTO, action: 'submit' | 'dismiss') => {
      // Use selectedTodo for todo-based variants, fall back to artifact.id for pending artifacts
      const animId = selectedTodo?.id ?? artifact.id;
      closeArtifactModal();

      if (action === 'submit' && !selectedTodo) {
        // Pending artifact submit: play sound immediately, show "Reading your response" for 5s, then animate out
        playTodoSound();
        setSubmittedItems((prev) => new Set(prev).add(animId));

        setTimeout(() => {
          setSubmittedItems((prev) => {
            const next = new Set(prev);
            next.delete(animId);
            return next;
          });

          // Now start the completing animation (skip sound — already played on submit)
          const todoEl = document.querySelector(
            `[data-todo-id="${animId}"]`,
          ) as HTMLElement | null;
          startCompletingAnimation(animId, todoEl, true);

          // After completing animation, remove item
          setTimeout(() => {
            setCompletingItems((prev) => {
              const next = new Set(prev);
              next.delete(animId);
              return next;
            });
            const actionedArtifact = {
              ...artifact,
              status: 'actioned' as const,
            };
            setPendingArtifacts((prev) =>
              prev.filter((a) => a.id !== artifact.id),
            );
            setActionedArtifacts((prev) => [actionedArtifact, ...prev]);
          }, 600);
        }, 1000);
      } else {
        // Dismiss: immediate completing animation
        const todoEl = document.querySelector(
          `[data-todo-id="${animId}"]`,
        ) as HTMLElement | null;
        startCompletingAnimation(animId, todoEl);

        // After animation completes, move artifact and update state
        setTimeout(() => {
          setCompletingItems((prev) => {
            const next = new Set(prev);
            next.delete(animId);
            return next;
          });
          const actionedArtifact = { ...artifact, status: 'actioned' as const };
          setPendingArtifacts((prev) =>
            prev.filter((a) => a.id !== artifact.id),
          );
          setActionedArtifacts((prev) => [actionedArtifact, ...prev]);
          if (selectedTodo) {
            const todoId = selectedTodo.id;
            setTodos((prev) =>
              prev.map((t) =>
                t.id === todoId
                  ? {
                      ...t,
                      status: 'complete' as const,
                      summary: '- [x] Cancelled by user',
                    }
                  : t,
              ),
            );
          }
        }, 600);
      }
    },
    [closeArtifactModal, playTodoSound, selectedTodo, startCompletingAnimation],
  );

  const handleSubmitArtifactResponse = useCallback(
    async (
      artifact: ResponseArtifactDTO,
      response: any,
      mandateOptions?: {
        createMandate: boolean;
        mandateItems: Array<{
          actionLine: string;
          descriptionLine: string;
          price?: number;
          price_currency?: string;
        }>;
      },
      cardSelection?: CardSelection,
    ) => {
      try {
        const token = await getAccessTokenSilently();
        const request: UpdateResponseArtifactRequest = {
          artifactId: artifact.id,
          status: 'actioned',
          userResponse: response,
          source: 'todo_list',
          ...(mandateOptions?.createMandate && {
            create_mandate: true,
            mandate_items: mandateOptions.mandateItems,
          }),
          ...(cardSelection && { card_selection: cardSelection }),
        };
        await api.patch<UpdateResponseArtifactResponse>(
          `/response-artifacts/${artifact.id}`,
          request,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
      } catch (error) {
        if (isOrganisationChargeError(error) || isHotelQuoteExpiredError(error))
          throw error;
        console.error('Error submitting artifact response:', error);
      }
    },
    [getAccessTokenSilently],
  );

  // Fetch data on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = await getAccessTokenSilently();

        const assigneeType = variant === 'waiting_on_user' ? 'user' : 'agent';
        const threadFilter = threadId ? `&threadId=${threadId}` : '';
        const parentThreadFilter = threadId
          ? `&parentThreadId=${threadId}`
          : '';
        const parentThreadQs = threadId ? `?parentThreadId=${threadId}` : '';
        const agentFilter = agentId ? `&agentId=${agentId}` : '';

        if (variant === 'waiting_on_user') {
          // For waiting_on_user variant: load artifacts + user todos
          const [pendingResponse, actionedResponse, userTodosResponse] =
            await Promise.all([
              api.get<GetUserArtifactsResponse>(
                `/response-artifacts/user/artifacts?status=pending${parentThreadFilter}${agentFilter}`,
                { headers: { Authorization: `Bearer ${token}` } },
              ),
              api.get<GetUserArtifactsResponse>(
                `/response-artifacts/user/artifacts?status=actioned${parentThreadFilter}${agentFilter}`,
                { headers: { Authorization: `Bearer ${token}` } },
              ),
              api.get<GetUserTodosResponse>(
                `/user-todos?assigneeType=${assigneeType}${threadFilter}${agentFilter}`,
                { headers: { Authorization: `Bearer ${token}` } },
              ),
            ]);
          setPendingArtifacts(pendingResponse.data.artifacts || []);
          setActionedArtifacts(actionedResponse.data.artifacts || []);
          setUserTodos(userTodosResponse.data.todos || []);
        } else {
          // For other variant: load sub-threads + user todos + temp todos
          const agentQs = agentId
            ? parentThreadQs
              ? `&agentId=${agentId}`
              : `?agentId=${agentId}`
            : '';
          const [todosResponse, userTodosResponse, tempTodosResponse] =
            await Promise.all([
              api.get<GetAllAssistantTodosResponse>(
                `/todos${parentThreadQs}${agentQs}`,
                {
                  headers: { Authorization: `Bearer ${token}` },
                },
              ),
              api.get<GetUserTodosResponse>(
                `/user-todos?assigneeType=${assigneeType}${threadFilter}${agentFilter}`,
                { headers: { Authorization: `Bearer ${token}` } },
              ),
              api.get<{ success: boolean; tempTodos: any[] }>(
                `/user-todos/temp${agentId ? `?agentId=${agentId}` : ''}`,
                { headers: { Authorization: `Bearer ${token}` } },
              ),
            ]);
          setTodos(todosResponse.data.todos);
          setUserTodos(userTodosResponse.data.todos || []);
          setTempTodos(
            (tempTodosResponse.data.tempTodos || []).map((t: any) => ({
              id: t.id,
              description: t.description,
              status: 'pending' as const,
              assigneeType: 'agent' as const,
              threadId: t.threadId,
              agentId: t.agentId,
              createdAt: t.createdAt,
              updatedAt: t.createdAt,
            })),
          );
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getAccessTokenSilently, variant, threadId, agentId]);

  // Fetch notes (information_dump artifacts) when the notes view is activated
  const fetchNotes = useCallback(
    async (offset = 0) => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<{
          success: boolean;
          artifacts: ResponseArtifactDTO[];
          hasMore: boolean;
        }>(`/response-artifacts/user/notes?limit=20&offset=${offset}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const fetched = res.data.artifacts || [];
        if (offset === 0) {
          setNotes(fetched);
        } else {
          setNotes((prev) => [...prev, ...fetched]);
        }
        setNotesHasMore(res.data.hasMore);
      } catch (err) {
        console.error('Error fetching notes:', err);
      }
    },
    [getAccessTokenSilently],
  );

  useEffect(() => {
    if (groupMode !== 'notes' || notesFetchedRef.current) return;
    notesFetchedRef.current = true;
    setNotesLoading(true);
    fetchNotes(0).finally(() => setNotesLoading(false));
  }, [groupMode, fetchNotes]);

  const handleLoadMoreNotes = useCallback(async () => {
    setNotesLoadingMore(true);
    await fetchNotes(notes.length);
    setNotesLoadingMore(false);
  }, [fetchNotes, notes.length]);

  // Optimistically prepend a new user todo, stripping any prior optimistic items
  useEffect(() => {
    if (!optimisticUserTodo) return;
    setUserTodos((prev) => {
      const withoutOptimistic = prev.filter(
        (t) => !t.id.startsWith('optimistic-'),
      );
      return [optimisticUserTodo, ...withoutOptimistic];
    });
  }, [optimisticUserTodo]);

  // Replace optimistic ID with real ID once POST confirms
  useEffect(() => {
    if (!confirmedTodoIds) return;
    const { optimisticId, realId } = confirmedTodoIds;
    setUserTodos((prev) =>
      prev.map((t) => (t.id === optimisticId ? { ...t, id: realId } : t)),
    );
  }, [confirmedTodoIds]);

  // Remove a user todo when it's been delegated to the agent
  useEffect(() => {
    if (!removedUserTodoId) return;
    setUserTodos((prev) => prev.filter((t) => t.id !== removedUserTodoId));
  }, [removedUserTodoId]);

  // Optimistically prepend a new agent temp todo (append, don't replace)
  useEffect(() => {
    if (!optimisticAgentTempTodo) return;
    setTempTodos((prev) => {
      if (prev.some((t) => t.id === optimisticAgentTempTodo.id)) return prev;
      return [optimisticAgentTempTodo, ...prev];
    });
  }, [optimisticAgentTempTodo]);

  // Replace optimistic agent temp todo ID with real temp todo ID
  useEffect(() => {
    if (!confirmedAgentTodoIds) return;
    const { optimisticId, realId } = confirmedAgentTodoIds;
    setTempTodos((prev) =>
      prev.map((t) => (t.id === optimisticId ? { ...t, id: realId } : t)),
    );
  }, [confirmedAgentTodoIds]);

  // Listen for real-time updates via company channel
  useEffect(() => {
    if (!pusherChannel) return;

    const handleSubThreadCreated = (data: SubThreadCreatedEvent) => {
      if (!data.subThread) return;
      if (threadId && data.parentThreadId !== threadId) return;

      const newTodo: AssistantTodoItem = {
        id: data.subThread.id,
        description: data.subThread.description,
        status: data.subThread.status as SubThreadStatus,
        summary: data.subThread.summary,
        completionEta: data.subThread.completionEta,
        steps: data.subThread.steps,
        createdAt: data.subThread.createdAt,
        updatedAt: data.subThread.updatedAt,
        parentThreadId: data.parentThreadId,
        parentThreadDescription: data.parentThreadDescription || 'Thread',
        parentThreadIsMainThread: data.parentThreadIsMainThread,
        parentThreadIsInternalMonologue: data.parentThreadIsInternalMonologue,
        parentThreadIsResponseArtifactThread:
          data.parentThreadIsResponseArtifactThread,
        informationDumps: data.subThread.informationDumps,
        latestArtifact: (data.subThread as AssistantTodoItem).latestArtifact,
      };

      // Remove any temp todo with matching description (the sub-thread replaces it)
      const subDesc = data.subThread.description?.toLowerCase().trim();
      setTempTodos((prev) =>
        prev.filter((t) => {
          const tempDesc = t.description?.toLowerCase().trim();
          return (
            tempDesc !== subDesc &&
            !subDesc?.includes(tempDesc) &&
            !tempDesc?.includes(subDesc)
          );
        }),
      );

      setTodos((prev) => {
        if (prev.some((t) => t.id === newTodo.id)) {
          return prev;
        }
        return [newTodo, ...prev];
      });

      // Trigger animation
      setAnimatingItems((prev) => new Set(prev).add(newTodo.id));
      setTimeout(() => {
        setAnimatingItems((prev) => {
          const newSet = new Set(prev);
          newSet.delete(newTodo.id);
          return newSet;
        });
      }, 400);
    };

    const handleSubThreadUpdate = (data: SubThreadUpdateEvent) => {
      if (!data.subThread) return;
      if (threadId && data.parentThreadId !== threadId) return;

      // Skip updates for items currently in completing animation
      if (completingItemsRef.current.has(data.subThread.id)) return;

      setTodos((prev) => {
        const existingIndex = prev.findIndex((t) => t.id === data.subThread.id);

        if (existingIndex === -1) {
          // New todo - add it
          const newTodo: AssistantTodoItem = {
            id: data.subThread.id,
            description: data.subThread.description,
            status: data.subThread.status as SubThreadStatus,
            summary: data.subThread.summary,
            completionEta: data.subThread.completionEta,
            steps: data.subThread.steps,
            createdAt: data.subThread.createdAt,
            updatedAt: data.subThread.updatedAt,
            parentThreadId: data.parentThreadId,
            parentThreadDescription: data.parentThreadDescription || 'Thread',
            parentThreadIsInternalMonologue:
              data.parentThreadIsInternalMonologue,
            parentThreadIsResponseArtifactThread:
              data.parentThreadIsResponseArtifactThread,
            latestArtifact: (data.subThread as AssistantTodoItem)
              .latestArtifact,
          };
          return [newTodo, ...prev];
        }

        const oldTodo = prev[existingIndex];
        const updatedTodo = {
          ...oldTodo,
          isArchivedThread:
            (data.subThread as AssistantTodoItem).isArchivedThread ??
            oldTodo.isArchivedThread,
        };
        const oldGroup = getGroupForTodo(oldTodo);
        const newGroup = getGroupForTodo(updatedTodo);

        // Trigger animation if group changed
        if (oldGroup !== newGroup) {
          setAnimatingItems((prevAnim) =>
            new Set(prevAnim).add(data.subThread.id),
          );
          setTimeout(() => {
            setAnimatingItems((prevAnim) => {
              const newSet = new Set(prevAnim);
              newSet.delete(data.subThread.id);
              return newSet;
            });
          }, 400);
        }

        // Trigger summary change animation
        if (oldTodo.summary !== data.subThread.summary) {
          setSummaryChangedItems((prevSum) =>
            new Set(prevSum).add(data.subThread.id),
          );
          setTimeout(() => {
            setSummaryChangedItems((prevSum) => {
              const newSet = new Set(prevSum);
              newSet.delete(data.subThread.id);
              return newSet;
            });
          }, 300);
        }

        // Update in place
        const updated = [...prev];
        updated[existingIndex] = {
          ...oldTodo,
          status: data.subThread.status as SubThreadStatus,
          summary: data.subThread.summary,
          completionEta: data.subThread.completionEta,
          steps: data.subThread.steps,
          updatedAt: data.subThread.updatedAt,
          informationDumps: data.subThread.informationDumps,
          latestArtifact: (data.subThread as AssistantTodoItem).latestArtifact,
        };
        return updated;
      });
    };

    pusherChannel.bind(
      AgentStreamEvents.SUB_THREAD_CREATED,
      handleSubThreadCreated,
    );
    pusherChannel.bind(
      AgentStreamEvents.SUB_THREAD_UPDATE,
      handleSubThreadUpdate,
    );

    return () => {
      pusherChannel.unbind(
        AgentStreamEvents.SUB_THREAD_CREATED,
        handleSubThreadCreated,
      );
      pusherChannel.unbind(
        AgentStreamEvents.SUB_THREAD_UPDATE,
        handleSubThreadUpdate,
      );
    };
  }, [pusherChannel, threadId]);

  // Listen for artifact events via centralized provider
  const {
    registerCreatedCallback,
    unregisterCreatedCallback,
    registerUpdatedCallback,
    unregisterUpdatedCallback,
  } = useResponseArtifactEvents();

  useEffect(() => {
    const callbackId = `central-todo-list-${variant}-${threadId || 'global'}`;

    registerCreatedCallback(
      callbackId,
      (data: ResponseArtifactCreatedEvent) => {
        if (!data.artifact || variant !== 'waiting_on_user') return;
        if (threadId && data.parentThreadId !== threadId) return;
        if (data.artifact.artifactType === 'information_dump') return;
        const dto: ResponseArtifactDTO = {
          ...data.artifact,
          createdAt: data.artifact.createdAt || new Date().toISOString(),
          updatedAt: data.artifact.updatedAt || new Date().toISOString(),
        };
        setPendingArtifacts((prev) => {
          if (prev.some((a) => a.id === dto.id)) return prev;
          return [dto, ...prev];
        });
      },
    );

    registerUpdatedCallback(
      callbackId,
      (data: ResponseArtifactUpdatedEvent) => {
        if (!data.artifactId || variant !== 'waiting_on_user') return;
        if (threadId && data.parentThreadId !== threadId) return;

        // Skip updates for items currently in submitted or completing animation
        if (
          submittedItemsRef.current.has(data.artifactId) ||
          completingItemsRef.current.has(data.artifactId)
        ) {
          return;
        }

        // Content update with full artifact (e.g. enhance / find_more)
        if (data.artifact && data.status === 'pending') {
          // Remove from actioned if it was there
          setActionedArtifacts((prev) =>
            prev.filter((a) => a.id !== data.artifactId),
          );
          setPendingArtifacts((prev) => {
            const exists = prev.some((a) => a.id === data.artifactId);
            if (exists) {
              return prev.map((a) =>
                a.id === data.artifactId ? { ...a, ...data.artifact } : a,
              );
            }
            return [...prev, data.artifact as ResponseArtifactDTO];
          });
          return;
        }

        // Handle linked artifact updates (from linkArtifactToBlankTodo)
        if (data.linkedArtifacts) {
          setPendingArtifacts((prev) =>
            prev.map((a) =>
              a.id === data.artifactId
                ? {
                    ...a,
                    linkedArtifactIds: data.linkedArtifactIds,
                    linkedArtifacts: data.linkedArtifacts,
                  }
                : a,
            ),
          );
          return;
        }

        if (data.status === 'actioned') {
          setPendingArtifacts((prev) =>
            prev.filter((a) => a.id !== data.artifactId),
          );
          if (!data.dismissed) {
            setActionedArtifacts((prev) => {
              if (prev.some((a) => a.id === data.artifactId)) return prev;
              return [
                {
                  id: data.artifactId,
                  status: 'actioned',
                } as ResponseArtifactDTO,
                ...prev,
              ];
            });
          }
        }
      },
    );

    return () => {
      unregisterCreatedCallback(callbackId);
      unregisterUpdatedCallback(callbackId);
    };
  }, [
    variant,
    threadId,
    registerCreatedCallback,
    unregisterCreatedCallback,
    registerUpdatedCallback,
    unregisterUpdatedCallback,
  ]);

  // Listen for new/updated information_dump artifacts to keep notes list live
  useEffect(() => {
    if (variant !== 'waiting_on_user') return;
    const cbId = 'central-todo-notes';

    registerCreatedCallback(cbId, (data: ResponseArtifactCreatedEvent) => {
      if (!data.artifact) return;
      if (data.artifact.artifactType !== 'information_dump') return;
      const dto: ResponseArtifactDTO = {
        ...data.artifact,
        createdAt: data.artifact.createdAt || new Date().toISOString(),
        updatedAt: data.artifact.updatedAt || new Date().toISOString(),
      };
      setNotes((prev) => {
        if (prev.some((n) => n.id === dto.id)) return prev;
        return [dto, ...prev];
      });
    });

    registerUpdatedCallback(cbId, (data: ResponseArtifactUpdatedEvent) => {
      if (!data.artifactId) return;
      setNotes((prev) =>
        prev.map((n) =>
          n.id === data.artifactId
            ? { ...n, status: data.status as ResponseArtifactDTO['status'] }
            : n,
        ),
      );
    });

    return () => {
      unregisterCreatedCallback(cbId);
      unregisterUpdatedCallback(cbId);
    };
  }, [
    variant,
    registerCreatedCallback,
    unregisterCreatedCallback,
    registerUpdatedCallback,
    unregisterUpdatedCallback,
  ]);

  const localizeTimestamp = (iso: string): string => {
    const target = new Date(iso);
    if (isNaN(target.getTime())) return iso;
    const now = new Date();
    const diffMs = target.getTime() - now.getTime();
    const absMs = Math.abs(diffMs);

    const timeStr = target.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });

    // Within the next 24 hours — just show the time
    if (diffMs > 0 && absMs < 24 * 60 * 60 * 1000) return timeStr;

    // Within the next 7 days — "Wednesday at 3:30 PM"
    if (diffMs > 0 && absMs < 7 * 24 * 60 * 60 * 1000) {
      const dayName = target.toLocaleDateString(undefined, {
        weekday: 'long',
      });
      return `${dayName} at ${timeStr}`;
    }

    // Further out — "12th December at 2 PM"
    return (
      target.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'long',
      }) + ` at ${timeStr}`
    );
  };

  const formatEta = (eta: string): string | null => {
    // New format: string with {timestamp}ISO{/timestamp} tags
    if (eta.includes('{timestamp}')) {
      return eta.replace(/\{timestamp\}([^{]+)\{\/timestamp\}/g, (_, iso) =>
        localizeTimestamp(iso.trim()),
      );
    }
    // Legacy fallback: bare ISO timestamp
    const target = new Date(eta);
    if (!isNaN(target.getTime())) return localizeTimestamp(eta);
    return eta;
  };

  const getActiveStep = (todo: AssistantTodoItem) => {
    const steps = todo.steps || [];
    return (
      steps.find((s) => s.active) ||
      steps.find((s) => s.status === 'in_progress') ||
      steps.find((s) => s.status === 'waiting_on_user') ||
      steps.find((s) => s.status === 'waiting_on_third_party') ||
      steps.find((s) => s.status === 'failed') ||
      steps.find((s) => s.status === 'pending')
    );
  };

  const resolveDisplayStatus = (
    todo: AssistantTodoItem,
    activeStep?: { status: string },
  ): string => {
    const stepStatus = activeStep?.status || todo.status;
    if (stepStatus === 'complete') return 'complete';
    if (stepStatus === 'failed' || stepStatus === 'cancelled_by_user')
      return 'failed';
    if (
      stepStatus === 'waiting_on_user' ||
      stepStatus === 'waiting_on_third_party'
    )
      return 'waiting';
    return 'processing';
  };

  const getStepIcon = (displayStatus: string) => {
    if (displayStatus === 'complete')
      return <CheckSmallIcon className='step-icon complete' />;
    if (displayStatus === 'failed')
      return <CrossIcon className='step-icon failed' />;
    if (displayStatus === 'waiting')
      return <HourglassIcon className='step-icon hourglass' />;
    return <TodoSpinnerIcon className='step-icon spinner' />;
  };

  const renderAssistantTodoItem = (todo: AssistantTodoItem) => {
    const isAnimating = animatingItems.has(todo.id);
    const isCompleting = completingItems.has(todo.id);
    const hasArtifact = !!todo.latestArtifact;
    const isClickable = hasArtifact && todo.status === 'waiting_on_user';
    const isTerminal =
      todo.status === 'complete' ||
      todo.status === 'failed' ||
      todo.status === 'cancelled_by_user';
    const isDismissable = !todo.isArchivedThread;
    const hasSummaryChanged = summaryChangedItems.has(todo.id);

    const activeStep = getActiveStep(todo);
    const displayStatus = resolveDisplayStatus(todo, activeStep);
    const stepText =
      activeStep?.description || todo.summary || 'Working on it...';
    const etaText = todo.completionEta ? formatEta(todo.completionEta) : null;

    return (
      <div
        key={todo.id}
        className={`todo-item agent-card ${todo.status} display-${displayStatus}${isAnimating ? ' animate-in' : ''}${isClickable ? ' clickable' : ''}${isCompleting ? ' completing' : ''}`}
        data-todo-id={todo.id}
        onClick={
          isClickable
            ? () => openArtifactModal(todo)
            : () => handleNavigateToThread(todo)
        }
        role='button'
        tabIndex={0}
      >
        <div className='agent-card-header'>
          {todo.iconImageUrl && (
            <img src={todo.iconImageUrl} alt='' className='todo-icon-image' />
          )}
          <span className='description'>{todo.description}</span>
          {isDismissable && (
            <button
              className='dismiss-btn'
              onClick={(e) => {
                e.stopPropagation();
                handleDismissClick(todo);
              }}
            >
              <CrossIcon />
            </button>
          )}
        </div>
        <div
          className={`agent-card-main${hasSummaryChanged ? ' crossfade' : ''}`}
        >
          <div className='active-step'>
            {getStepIcon(displayStatus)}
            <span>{stepText}</span>
          </div>
        </div>
        {etaText && (
          <div className='agent-card-footer'>
            <ClockIcon className='clock-icon' />
            <span>{etaText}</span>
          </div>
        )}
        {todo.informationDumps && todo.informationDumps.length > 0 && (
          <div className='agent-card-research'>
            {todo.informationDumps.map((dump) =>
              dump.projectAppId ? (
                <div
                  key={dump.id}
                  className='research-link'
                  onClick={(e) => {
                    e.stopPropagation();
                    setLinkedResearchAppId(dump.projectAppId!);
                    setLinkedResearchArtifactId(dump.id);
                    setLinkedResearchOpen(true);
                  }}
                  role='button'
                  tabIndex={0}
                >
                  <DocumentTextIcon className='document-icon' />
                  <span>{dump.title || 'View Research'}</span>
                </div>
              ) : null,
            )}
          </div>
        )}
      </div>
    );
  };

  const { openPlanModal } = usePlanModal();

  const handleToggleUserTodo = async (todo: UserTodoItem) => {
    // isSetupPlan todos open the plan selection modal instead of toggling
    if (todo.isSetupPlan && todo.status !== 'complete') {
      openPlanModal();
      return;
    }

    const newStatus = todo.status === 'pending' ? 'complete' : 'pending';

    if (newStatus === 'complete') {
      // Capture height and start completing animation + sound
      const todoEl = document.querySelector(
        `[data-todo-id="${todo.id}"]`,
      ) as HTMLElement | null;
      startCompletingAnimation(todo.id, todoEl);

      // Update state after animation completes
      setTimeout(() => {
        setCompletingItems((prev) => {
          const next = new Set(prev);
          next.delete(todo.id);
          return next;
        });
        setUserTodos((prev) =>
          prev.map((t) => (t.id === todo.id ? { ...t, status: newStatus } : t)),
        );
      }, 600);
    } else {
      // Uncompleting — instant, no animation
      setUserTodos((prev) =>
        prev.map((t) => (t.id === todo.id ? { ...t, status: newStatus } : t)),
      );
    }

    // Skip API call for optimistic items
    if (todo.id.startsWith('optimistic-')) return;

    try {
      const token = await getAccessTokenSilently();
      await api.patch(
        `/user-todos/${todo.id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (err) {
      console.error('Error updating user todo:', err);
      // Revert on failure
      setCompletingItems((prev) => {
        const next = new Set(prev);
        next.delete(todo.id);
        return next;
      });
      setUserTodos((prev) =>
        prev.map((t) => (t.id === todo.id ? { ...t, status: todo.status } : t)),
      );
    }
  };

  const handleToggleBlankTodo = async (artifact: ResponseArtifactDTO) => {
    // If artifact has linked research and user hasn't seen the warning, show modal
    const hasLinkedResearch = (artifact.linkedArtifacts?.length ?? 0) > 0;
    if (
      hasLinkedResearch &&
      !hasSeenModal('dismiss_blank_todo_research_warning')
    ) {
      setBlankTodoToComplete(artifact);
      setResearchModalOpen(true);
      return;
    }

    completeBlankTodo(artifact);
  };

  const completeBlankTodo = async (artifact: ResponseArtifactDTO) => {
    const todoEl = document.querySelector(
      `[data-todo-id="${artifact.id}"]`,
    ) as HTMLElement | null;
    startCompletingAnimation(artifact.id, todoEl);

    setTimeout(() => {
      setCompletingItems((prev) => {
        const next = new Set(prev);
        next.delete(artifact.id);
        return next;
      });
      const actionedArtifact = {
        ...artifact,
        status: 'actioned' as const,
      };
      setPendingArtifacts((prev) => prev.filter((a) => a.id !== artifact.id));
      setActionedArtifacts((prev) => [actionedArtifact, ...prev]);
    }, 600);

    try {
      const token = await getAccessTokenSilently();
      const request: UpdateResponseArtifactRequest = {
        artifactId: artifact.id,
        status: 'actioned',
        userResponse: { completed: true },
        source: 'todo_list',
      };
      await api.patch<UpdateResponseArtifactResponse>(
        `/response-artifacts/${artifact.id}`,
        request,
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (error) {
      console.error('Error completing blank todo:', error);
      // Revert on failure
      setCompletingItems((prev) => {
        const next = new Set(prev);
        next.delete(artifact.id);
        return next;
      });
      setPendingArtifacts((prev) => {
        if (prev.some((a) => a.id === artifact.id)) return prev;
        return [artifact, ...prev];
      });
      setActionedArtifacts((prev) => prev.filter((a) => a.id !== artifact.id));
    }
  };

  const renderUserTodoItem = (todo: UserTodoItem) => {
    const isComplete = todo.status === 'complete';
    const isCompleting = completingItems.has(todo.id);

    return (
      <div
        key={todo.id}
        className={`todo-item ${todo.isSetupPlan && !isComplete ? 'waiting_on_user' : 'user-added'}${isComplete ? ' complete' : ''}${isCompleting ? ' completing' : ''}`}
        data-todo-id={todo.id}
        draggable={
          !todo.isSetupPlan && variant === 'waiting_on_user' && !isComplete
        }
        onDragStart={
          !todo.isSetupPlan && variant === 'waiting_on_user' && !isComplete
            ? (e) => {
                const data: DraggedTaskData = {
                  type: 'userTodo',
                  id: todo.id,
                  description: todo.description,
                };
                e.dataTransfer.setData(
                  'application/json',
                  JSON.stringify(data),
                );
                e.dataTransfer.effectAllowed = 'move';
              }
            : undefined
        }
        onClick={
          todo.isSetupPlan && !isComplete
            ? () => handleToggleUserTodo(todo)
            : undefined
        }
        role={todo.isSetupPlan && !isComplete ? 'button' : undefined}
        tabIndex={todo.isSetupPlan && !isComplete ? 0 : undefined}
      >
        <div
          className={`check-container ${todo.isSetupPlan ? 'waiting_on_user' : `user-added${isComplete ? ' complete' : ''}`}`}
          onClick={
            !todo.isSetupPlan ? () => handleToggleUserTodo(todo) : undefined
          }
          role={!todo.isSetupPlan ? 'button' : undefined}
          tabIndex={!todo.isSetupPlan ? 0 : undefined}
        >
          <div className='circle'>
            {(isComplete || isCompleting) && <CheckSmallIcon />}
          </div>
        </div>
        <div className='item-wrapper'>
          <div className='item-header'>
            <span className='description'>{todo.description}</span>
            {!threadId &&
              groupMode !== 'project-grouped' &&
              todo.threadDescription && (
                <a
                  className='parent-thread inline'
                  href={`/conversations?thread_id=${todo.threadId}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!e.metaKey && !e.ctrlKey) {
                      e.preventDefault();
                      if (onNavigateToThread && todo.threadId) {
                        onNavigateToThread(todo.threadId, todo.threadId);
                      }
                    }
                  }}
                >
                  <ChatThreadIcon />
                  {todo.threadDescription}
                </a>
              )}
          </div>
          {!isComplete && (
            <div className='item-content'>
              {todo.dueDate && (
                <div className='status'>
                  <div className='current-status user-added'>
                    <div className='status-item'>
                      <span className='due-date'>
                        <CalendarOutlineIcon className='calendar-icon' />
                        {formatDueDate(todo.dueDate)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleDismissTempTodo = useCallback(
    async (todo: UserTodoItem) => {
      // Capture height and start completing animation + sound
      const todoEl = document.querySelector(
        `[data-todo-id="${todo.id}"]`,
      ) as HTMLElement | null;
      startCompletingAnimation(todo.id, todoEl);

      // Remove after animation completes
      setTimeout(() => {
        setCompletingItems((prev) => {
          const next = new Set(prev);
          next.delete(todo.id);
          return next;
        });
        setTempTodos((prev) => prev.filter((t) => t.id !== todo.id));
      }, 600);

      try {
        const accessToken = await getAccessTokenSilently();
        await api.delete(`/user-todos/temp/${todo.id}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
      } catch (err) {
        console.error('Error dismissing temp todo:', err);
      }
    },
    [getAccessTokenSilently],
  );

  const renderTempTodoItem = (todo: UserTodoItem) => {
    return (
      <div
        key={todo.id}
        className={`todo-item processing${completingItems.has(todo.id) ? ' completing' : ''}`}
        data-todo-id={todo.id}
      >
        <div
          className='check-container processing temporary dismissable'
          onClick={(e) => {
            e.stopPropagation();
            handleDismissTempTodo(todo);
          }}
          role='button'
          tabIndex={0}
        >
          <div className='circle'>
            {completingItems.has(todo.id) && <CheckSmallIcon />}
          </div>
        </div>
        <div className='item-wrapper'>
          <div className='item-header'>
            <span className='description'>{todo.description}</span>
          </div>
          <div className='item-content'>
            <div className='status'>
              <div className='current-status processing'>
                <div className='status-item'>
                  <TodoSpinnerIcon className='spinner' />
                  <span>Reading todo</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Split user todos into active/done
  const activeUserTodos = userTodos.filter((t) => t.status === 'pending');
  const doneUserTodos = userTodos.filter((t) => t.status === 'complete');

  // Unified sorted list for waiting_on_user active items
  // Sort order: 1) success outcomes, 2) failed outcomes, 3) items with due dates (ascending), 4) everything else by createdAt (newest first)
  type UnifiedActiveItem =
    | { kind: 'userTodo'; data: UserTodoItem }
    | { kind: 'artifact'; data: ResponseArtifactDTO };

  const sortedActiveItems = useMemo((): UnifiedActiveItem[] => {
    if (variant !== 'waiting_on_user') return [];

    const items: UnifiedActiveItem[] = [];

    activeUserTodos.forEach((todo) => {
      items.push({ kind: 'userTodo', data: todo });
    });

    pendingArtifacts.forEach((artifact) => {
      items.push({ kind: 'artifact', data: artifact });
    });

    const getTier = (item: UnifiedActiveItem): number => {
      if ((item.data as any).dueDate) return 2;
      return 3;
    };

    items.sort((a, b) => {
      const aTier = getTier(a);
      const bTier = getTier(b);

      // Different tiers: lower tier number = higher priority
      if (aTier !== bTier) return aTier - bTier;

      // Within tier 2 (due dates): sort by due date ascending
      if (aTier === 2) {
        const diff =
          new Date((a.data as any).dueDate!).getTime() -
          new Date((b.data as any).dueDate!).getTime();
        if (diff !== 0) return diff;
      }

      // Within same tier: sort by createdAt descending (newest first)
      return (
        new Date(b.data.createdAt).getTime() -
        new Date(a.data.createdAt).getTime()
      );
    });

    return items;
  }, [variant, activeUserTodos, pendingArtifacts]);

  // Group active items by project (parent thread) for project-grouped view
  const projectGroups = useMemo(() => {
    if (groupMode !== 'project-grouped') return [];

    const projectMap = new Map<
      string,
      {
        title: string;
        threadId: string;
        assistantTodos: AssistantTodoItem[];
        userTodos: UserTodoItem[];
        tempTodos: UserTodoItem[];
        pendingArtifacts: ResponseArtifactDTO[];
      }
    >();

    const getOrCreateProject = (threadId: string, title: string) => {
      if (!projectMap.has(threadId)) {
        projectMap.set(threadId, {
          title,
          threadId,
          assistantTodos: [],
          userTodos: [],
          tempTodos: [],
          pendingArtifacts: [],
        });
      }
      return projectMap.get(threadId)!;
    };

    const ungroupedKey = '__ungrouped__';

    // Group assistant todos by parentThreadId (internal monologue or no description goes to ungrouped)
    groupedTodos.active.forEach((todo) => {
      const hasDescription =
        !todo.parentThreadIsInternalMonologue && todo.parentThreadDescription;
      const key = hasDescription
        ? todo.parentThreadId || ungroupedKey
        : ungroupedKey;
      const title = hasDescription ? todo.parentThreadDescription! : 'Misc';
      getOrCreateProject(key, title).assistantTodos.push(todo);
    });

    // Group active user todos by threadId
    activeUserTodos.forEach((todo) => {
      const key = todo.threadDescription
        ? todo.threadId || ungroupedKey
        : ungroupedKey;
      const title = todo.threadDescription || 'Misc';
      getOrCreateProject(key, title).userTodos.push(todo);
    });

    // Group temp todos by threadId
    tempTodos.forEach((todo) => {
      const key = todo.threadDescription
        ? todo.threadId || ungroupedKey
        : ungroupedKey;
      const title = todo.threadDescription || 'Misc';
      getOrCreateProject(key, title).tempTodos.push(todo);
    });

    // Group pending artifacts by parentThreadId (internal monologue or no description goes to ungrouped)
    pendingArtifacts.forEach((artifact) => {
      const hasDescription =
        !artifact.parentThreadIsInternalMonologue &&
        artifact.parentThreadDescription;
      const key = hasDescription
        ? artifact.parentThreadId || ungroupedKey
        : ungroupedKey;
      const title = hasDescription ? artifact.parentThreadDescription! : 'Misc';
      getOrCreateProject(key, title).pendingArtifacts.push(artifact);
    });

    // Sort alphabetically, with Misc always last
    const groups = Array.from(projectMap.values());
    groups.sort((a, b) => {
      if (a.threadId === ungroupedKey) return 1;
      if (b.threadId === ungroupedKey) return -1;
      return a.title.localeCompare(b.title);
    });
    return groups;
  }, [
    groupMode,
    groupedTodos.active,
    activeUserTodos,
    tempTodos,
    pendingArtifacts,
  ]);

  const renderGroup = (
    groupKey: string,
    title: string,
    items: AssistantTodoItem[],
    showHeader: boolean = true,
  ) => {
    if (items.length === 0) return null;

    const isAccordion = groupKey === 'done';
    const isExpanded = groupKey === 'done' ? doneExpanded : true;

    return (
      <div className={`todo-group ${groupKey}${isExpanded ? ' expanded' : ''}`}>
        {showHeader && (
          <div
            className={`group-header${isAccordion ? ' accordion' : ''}`}
            onClick={
              isAccordion ? () => setDoneExpanded(!doneExpanded) : undefined
            }
            role={isAccordion ? 'button' : undefined}
            tabIndex={isAccordion ? 0 : undefined}
          >
            <span className='group-title'>{title}</span>
            {isAccordion && (
              <>
                <div className='counter'>{items.length}</div>
                <ChevronDownIcon
                  className={`chevron${isExpanded ? ' expanded' : ''}`}
                />
              </>
            )}
          </div>
        )}
        {(!isAccordion || isExpanded) && (
          <div className='group-content'>
            {items.map((todo) => renderAssistantTodoItem(todo))}
          </div>
        )}
      </div>
    );
  };

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      if (variant !== 'other') return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setDragOver(true);
    },
    [variant],
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent) => {
      if (variant !== 'other') return;
      if (e.currentTarget.contains(e.relatedTarget as Node)) return;
      setDragOver(false);
    },
    [variant],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      if (variant !== 'other') return;
      e.preventDefault();
      setDragOver(false);
      try {
        const raw = e.dataTransfer.getData('application/json');
        if (!raw) return;
        const data: DraggedTaskData = JSON.parse(raw);
        onTaskDropped?.(data);
      } catch {
        // ignore bad data
      }
    },
    [variant, onTaskDropped],
  );

  if (loading) {
    return (
      <div className='central-todo-list'>
        <TasksSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className='central-todo-list'>
        <ErrorState title='Failed to load tasks' subtitle={error} />
      </div>
    );
  }

  // Calculate done count including user todos
  const doneCount =
    variant === 'waiting_on_user'
      ? actionedArtifacts.length + doneUserTodos.length
      : groupedTodos.done.length + doneUserTodos.length;

  // Render linked research links for an artifact (supports multiple)
  const renderLinkedResearch = (artifact: ResponseArtifactDTO) => {
    const linked = artifact.linkedArtifacts || [];
    if (linked.length === 0) return null;

    return (
      <div className='item-content'>
        <div className='status'>
          <div className='current-status waiting_on_user'>
            {linked.map((la) =>
              la.projectAppId ? (
                <div
                  key={la.id}
                  className='status-item linked-research'
                  onClick={(e) => {
                    e.stopPropagation();
                    setLinkedResearchAppId(la.projectAppId!);
                    setLinkedResearchArtifactId(la.id);
                    setLinkedResearchOpen(true);
                  }}
                  role='button'
                  tabIndex={0}
                >
                  <DocumentTextIcon className='document-icon' />
                  <span>{la.title || 'View Research'}</span>
                </div>
              ) : null,
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render a single pending artifact item (for waiting_on_user variant)
  const renderPendingArtifact = (artifact: ResponseArtifactDTO) => {
    const displayTitle =
      artifact.todoDescription || artifact.title || 'Pending task';
    const isMainThread = artifact.parentThreadIsMainThread;
    const isInternalMonologue = artifact.parentThreadIsInternalMonologue;
    const threadDescription = artifact.parentThreadDescription;
    const isSubmitted = submittedItems.has(artifact.id);
    const isCompleting = completingItems.has(artifact.id);
    const isBlankTodo = artifact.artifactType === 'blank_todo';
    // Blank todo: render with checkbox styling (like user todos)
    if (isBlankTodo) {
      return (
        <div
          key={artifact.id}
          className={`todo-item user-added${isCompleting ? ' completing' : ''}`}
          data-todo-id={artifact.id}
        >
          <div
            className='check-container user-added'
            onClick={() => handleToggleBlankTodo(artifact)}
            role='button'
            tabIndex={0}
          >
            <div className='circle'>{isCompleting && <CheckSmallIcon />}</div>
          </div>
          <div className='item-wrapper'>
            <div className='item-header'>
              <span className='description'>{displayTitle}</span>
              {!threadId &&
                groupMode !== 'project-grouped' &&
                !isMainThread &&
                !isInternalMonologue &&
                !artifact.parentThreadIsResponseArtifactThread &&
                threadDescription && (
                  <a
                    className='parent-thread inline'
                    href={`/conversations?thread_id=${artifact.parentThreadId}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!e.metaKey && !e.ctrlKey) {
                        e.preventDefault();
                        if (onNavigateToThread) {
                          onNavigateToThread(
                            artifact.parentThreadId,
                            artifact.parentThreadId,
                          );
                        }
                      }
                    }}
                  >
                    <ChatThreadIcon />
                    {threadDescription}
                  </a>
                )}
            </div>
            {(artifact as any).dueDate && (
              <div className='item-content'>
                <div className='status'>
                  <div className='current-status user-added'>
                    <div className='status-item'>
                      <span className='due-date'>
                        <CalendarOutlineIcon className='calendar-icon' />
                        {formatDueDate((artifact as any).dueDate)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {renderLinkedResearch(artifact)}
          </div>
        </div>
      );
    }

    return (
      <div
        key={artifact.id}
        className={`todo-item waiting_on_user${isCompleting ? ' completing' : ''}${isSubmitted ? ' submitted' : ''}`}
        data-todo-id={artifact.id}
        onClick={
          !isSubmitted && !isCompleting
            ? () => {
                setFullArtifact(artifact);
                setArtifactModalOpen(true);
              }
            : undefined
        }
        role={!isSubmitted && !isCompleting ? 'button' : undefined}
        tabIndex={!isSubmitted && !isCompleting ? 0 : undefined}
      >
        <div
          className={`check-container waiting_on_user${isSubmitted ? ' submitted' : ''}`}
        >
          <div className='circle'>
            {isSubmitted && <TodoSpinnerIcon className='spinner' />}
            {isCompleting && <CheckSmallIcon />}
          </div>
        </div>
        <div className='item-wrapper'>
          <div className='item-header'>
            <span className='description'>{displayTitle}</span>
            {!threadId &&
              groupMode !== 'project-grouped' &&
              !isMainThread &&
              !isInternalMonologue &&
              !artifact.parentThreadIsResponseArtifactThread &&
              threadDescription && (
                <a
                  className='parent-thread inline'
                  href={`/conversations?thread_id=${artifact.parentThreadId}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!e.metaKey && !e.ctrlKey) {
                      e.preventDefault();
                      if (onNavigateToThread) {
                        onNavigateToThread(
                          artifact.parentThreadId,
                          artifact.parentThreadId,
                        );
                      }
                    }
                  }}
                >
                  <ChatThreadIcon />
                  {threadDescription}
                </a>
              )}
          </div>
          {!isSubmitted && (artifact as any).dueDate && (
            <div className='item-content'>
              <div className='status'>
                <div className='current-status user-added'>
                  <div className='status-item'>
                    <span className='due-date'>
                      <CalendarOutlineIcon className='calendar-icon' />
                      {formatDueDate((artifact as any).dueDate)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
          {!isSubmitted && renderLinkedResearch(artifact)}
          {isSubmitted && (
            <div className='item-content'>
              <div className='status'>
                <div className='current-status processing'>
                  <div className='status-item'>
                    <FlagIcon className='flag-icon' />
                    <span>Reading your response</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render a single actioned artifact item
  const renderActionedArtifact = (artifact: ResponseArtifactDTO) => {
    // Use todoDescription as the display title for done items
    const displayTitle =
      artifact.todoDescription || artifact.title || 'Completed';

    return (
      <div
        key={artifact.id}
        className='todo-item complete'
        onClick={() => {
          setFullArtifact(artifact);
          setArtifactModalOpen(true);
        }}
        role='button'
        tabIndex={0}
      >
        <div className='check-container user-added complete'>
          <div className='circle'>
            <CheckSmallIcon />
          </div>
        </div>
        <div className='item-wrapper'>
          <div className='item-header'>
            <span className='description'>{displayTitle}</span>
          </div>
        </div>
      </div>
    );
  };

  // Check if list has any items to show
  const hasItems =
    variant === 'waiting_on_user'
      ? pendingArtifacts.length > 0 ||
        actionedArtifacts.length > 0 ||
        userTodos.length > 0
      : groupedTodos.active.length > 0 ||
        groupedTodos.done.length > 0 ||
        userTodos.length > 0 ||
        tempTodos.length > 0;

  // True when items exist but everything is done (nothing active/pending)
  const allItemsDone =
    hasItems &&
    (variant === 'waiting_on_user'
      ? pendingArtifacts.length === 0 && activeUserTodos.length === 0
      : groupedTodos.active.length === 0 &&
        activeUserTodos.length === 0 &&
        tempTodos.length === 0);

  return (
    <div
      className={`central-todo-list ${variant}${dragOver ? ' drag-over' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {title && (
        <h2 className='list-title'>
          <div className='left-out-wrapper'>
            <div className='inner-wrapper'>
              {agentImageUrl && (
                <div className='dreamie-image-wrapper'>
                  <img src={agentImageUrl} alt={agentName || 'Agent'} />
                </div>
              )}
              {title}
            </div>
            {!threadId && (
              <div className='group-mode-switcher'>
                <div
                  className={`switcher-indicator${groupMode === 'project-grouped' ? ' project-grouped' : groupMode === 'notes' ? ' notes' : ''}`}
                />
                <button
                  className='switcher-option'
                  onClick={() => updateGroupMode('none')}
                >
                  <ListViewIcon />
                </button>
                <button
                  className='switcher-option'
                  onClick={() => updateGroupMode('project-grouped')}
                >
                  <ProjectGroupedViewIcon />
                </button>
                {variant === 'waiting_on_user' && (
                  <button
                    className='switcher-option'
                    onClick={() => updateGroupMode('notes')}
                  >
                    <DocumentLinesIcon />
                  </button>
                )}
              </div>
            )}
          </div>
        </h2>
      )}
      <div className='todos-container'>
        {groupMode === 'notes' ? (
          <div className='notes-list'>
            {notesLoading ? (
              <TasksSkeleton />
            ) : notes.length === 0 ? (
              <div className='empty-notes'>
                <p>No research notes yet</p>
              </div>
            ) : (
              <>
                {notes.map((note) => (
                  <div
                    key={note.id}
                    className='note-item'
                    onClick={() => {
                      if (note.projectAppId) {
                        setLinkedResearchAppId(note.projectAppId);
                        setLinkedResearchArtifactId(note.id);
                        setLinkedResearchOpen(true);
                      }
                    }}
                    role='button'
                    tabIndex={0}
                  >
                    <DocumentTextIcon className='document-icon' />
                    <span>{note.title || 'Untitled'}</span>
                  </div>
                ))}
                {notesHasMore && (
                  <button
                    className='load-more-btn'
                    onClick={handleLoadMoreNotes}
                    disabled={notesLoadingMore}
                  >
                    {notesLoadingMore ? 'Loading…' : 'Load more'}
                  </button>
                )}
              </>
            )}
          </div>
        ) : groupMode === 'project-grouped' ? (
          <>
            {projectGroups.length > 0 && (
              <div className='todo-group active expanded'>
                <div className='group-content'>
                  {projectGroups.map((project) => {
                    const itemCount =
                      project.assistantTodos.length +
                      project.userTodos.length +
                      project.tempTodos.length +
                      project.pendingArtifacts.length;
                    if (itemCount === 0) return null;
                    return (
                      <div key={project.threadId} className='project-group'>
                        <div className='project-header'>
                          {project.title !== 'Misc' && <ChatThreadIcon />}
                          <span className='project-title'>{project.title}</span>
                          <div className='counter'>
                            <span>{itemCount}</span>
                          </div>
                        </div>
                        {project.tempTodos.map((todo) =>
                          renderTempTodoItem(todo),
                        )}
                        {project.userTodos.map((todo) =>
                          renderUserTodoItem(todo),
                        )}
                        {project.pendingArtifacts.map((artifact) =>
                          renderPendingArtifact(artifact),
                        )}
                        {project.assistantTodos.map((todo) =>
                          renderAssistantTodoItem(todo),
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {!hideDone &&
              (groupedTodos.done.length > 0 ||
                doneUserTodos.length > 0 ||
                actionedArtifacts.length > 0) && (
                <div
                  className={`todo-group done${doneExpanded ? ' expanded' : ''}`}
                >
                  <div
                    className='group-header accordion'
                    onClick={() => setDoneExpanded(!doneExpanded)}
                    role='button'
                    tabIndex={0}
                  >
                    <span className='group-title'>Done</span>
                    <span className='counter'>{doneCount}</span>
                    <ChevronDownIcon
                      className={`chevron${doneExpanded ? ' expanded' : ''}`}
                    />
                  </div>
                  {doneExpanded && (
                    <div className='group-content'>
                      {doneUserTodos.map((todo) => renderUserTodoItem(todo))}
                      {actionedArtifacts.map((artifact) =>
                        renderActionedArtifact(artifact),
                      )}
                      {groupedTodos.done.map((todo) =>
                        renderAssistantTodoItem(todo),
                      )}
                    </div>
                  )}
                </div>
              )}
          </>
        ) : variant === 'waiting_on_user' ? (
          <>
            {(pendingArtifacts.length > 0 || activeUserTodos.length > 0) && (
              <div className='todo-group active expanded'>
                <div className='group-content'>
                  {sortedActiveItems.map((item) =>
                    item.kind === 'userTodo'
                      ? renderUserTodoItem(item.data)
                      : renderPendingArtifact(item.data),
                  )}
                </div>
              </div>
            )}
            {!hideDone &&
              (actionedArtifacts.length > 0 || doneUserTodos.length > 0) && (
                <div
                  className={`todo-group done${doneExpanded ? ' expanded' : ''}`}
                >
                  <div
                    className='group-header accordion'
                    onClick={() => setDoneExpanded(!doneExpanded)}
                    role='button'
                    tabIndex={0}
                  >
                    <span className='group-title'>Done</span>
                    <span className='counter'>{doneCount}</span>
                    <ChevronDownIcon
                      className={`chevron${doneExpanded ? ' expanded' : ''}`}
                    />
                  </div>
                  {doneExpanded && (
                    <div className='group-content'>
                      {doneUserTodos.map((todo) => renderUserTodoItem(todo))}
                      {actionedArtifacts.map((artifact) =>
                        renderActionedArtifact(artifact),
                      )}
                    </div>
                  )}
                </div>
              )}
          </>
        ) : (
          <>
            {(groupedTodos.active.length > 0 ||
              activeUserTodos.length > 0 ||
              tempTodos.length > 0) && (
              <div className='todo-group active expanded'>
                <div className='group-content'>
                  {tempTodos.map((todo) => renderTempTodoItem(todo))}
                  {activeUserTodos.map((todo) => renderUserTodoItem(todo))}
                  {groupedTodos.active.map((todo) =>
                    renderAssistantTodoItem(todo),
                  )}
                </div>
              </div>
            )}
            {!hideDone &&
              (groupedTodos.done.length > 0 || doneUserTodos.length > 0) && (
                <div
                  className={`todo-group done${doneExpanded ? ' expanded' : ''}`}
                >
                  <div
                    className='group-header accordion'
                    onClick={() => setDoneExpanded(!doneExpanded)}
                    role='button'
                    tabIndex={0}
                  >
                    <span className='group-title'>Done</span>
                    <span className='counter'>{doneCount}</span>
                    <ChevronDownIcon
                      className={`chevron${doneExpanded ? ' expanded' : ''}`}
                    />
                  </div>
                  {doneExpanded && (
                    <div className='group-content'>
                      {doneUserTodos.map((todo) => renderUserTodoItem(todo))}
                      {groupedTodos.done.map((todo) =>
                        renderAssistantTodoItem(todo),
                      )}
                    </div>
                  )}
                </div>
              )}
          </>
        )}
        {!hasItems && variant === 'other' && !hideDone && (
          <div className='empty-todo-state'>
            <img
              src='/splicons/todo.png'
              alt='Todo'
              className='empty-state-icon'
            />
            <div className='empty-explain'>
              <h1>It's time to save time</h1>
              <p>
                Get your secretary working on your tasks. Try adding your a task
                and see how it works
              </p>
            </div>
            <button className='empty-state-add-btn' onClick={onAddTodo}>
              <PlusAddIcon />
              Add task
            </button>
          </div>
        )}
        {(!hasItems || allItemsDone) && variant !== 'other' && (
          <div className='todo-empty-with-done'>
            <p>There's nothing to-do here</p>
            <small>Your todo list is empty</small>
          </div>
        )}
        {(allItemsDone || (!hasItems && hideDone)) && variant === 'other' && (
          <div className='todo-empty-with-done'>
            <p>It's time to save time</p>
            <small>{agentName}'s tasks will appear here</small>
          </div>
        )}
      </div>

      {/* Research Still There Modal (blank todos with linked research) */}
      <ResearchStillThereModal
        show={researchModalOpen}
        artifact={blankTodoToComplete}
        onClose={() => {
          setResearchModalOpen(false);
          setBlankTodoToComplete(null);
        }}
        onConfirm={(artifact) => {
          setResearchModalOpen(false);
          setBlankTodoToComplete(null);
          completeBlankTodo(artifact);
        }}
      />

      {/* Dismiss Todo Modal */}
      <DismissTodoModal
        show={dismissModalOpen}
        todo={todoToDismiss}
        onClose={handleDismissModalClose}
        onConfirm={handleDismissConfirm}
      />

      {/* Linked Research Overlay */}
      <ProjectAppLoadingProvider>
        <ResearchBlockModal
          show={linkedResearchOpen}
          onClose={() => {
            setLinkedResearchOpen(false);
            setLinkedResearchAppId(null);
            setLinkedResearchArtifactId(null);
          }}
          appId={linkedResearchAppId || ''}
          artifactId={linkedResearchArtifactId || undefined}
          backLabel='Back to Todo list'
        />
      </ProjectAppLoadingProvider>

      {/* Response Artifact Modal */}
      <ResponseArtifactModal
        show={artifactModalOpen && !!fullArtifact && !loadingArtifact}
        onClose={closeArtifactModal}
        artifacts={modalArtifacts}
        initialIndex={0}
        onSubmitResponse={handleSubmitArtifactResponse}
        onOptimisticAction={handleOptimisticAction}
        readOnly={fullArtifact?.status === 'actioned'}
        navigateOnChat
      />
    </div>
  );
};

export default CentralTodoList;
